/* DrinksNation cart (S3-05 deposit sync, S3-06 drawer + cart page; v1.1 §5.3, the build rules non-negotiable 4;
   7 Sep 2026: the small-order service fee line, qty 0 or 1 while the drinks at carton prices are under
   S$30, kept in step the same way as the deposit — feePlan / nextPlan below, fees.ts is the rule).
   Keeps the hidden BCRS deposit line at qty = Σ(pack_qty × cartons) over every line whose product
   has dnation.bcrs = true. Data comes from the JSON script tags that snippets/dn-cart-data.liquid
   renders (the Ajax cart carries no metafields); after every add or change the section is
   re-fetched, the plan is computed, and one /cart/add or /cart/change request sets the deposit
   line (added when missing, removed at zero). All cart requests run through one queue, one in
   flight; a change that lands meanwhile queues one more run. The deposit line never gets a
   stepper: Liquid renders none and this file strips any that a re-render might bring back. The
   header count shows cartons, not units.
   S3-06: <cart-drawer> element (the three methods Dawn's product-form.js calls after an add),
   line steppers and remove links (/cart/change), delivery notes → cart attributes (/cart/update),
   cart links open the drawer. Every request asks for the page's cart sections
   (data-dn-cart-section) and swaps their .js-contents, so totals and the nudge are always Liquid's.
   Vanilla JS, no framework. Loads after pubsub.js (subscribe/PUB_SUB_EVENTS) and dn-ui.js.
   The pure maths (depositQty, cartons, plan) is exported for node tests: qa/unit/dn-cart.test.mjs.
   E-vouchers (ADR-044, 1 Oct 2026): a gift-card line (gift_card true in dn-cart-data) is not drinks. It owes no
   deposit, counts towards neither the fee nor the cartons on the van, and the basket holds at most
   config.gift_card_max of them (10 online): the steppers stop there, so the checkout's refusal is rarely met. */
(function (root, factory) {
  var api = factory(root);
  if (typeof module === 'object' && module.exports) module.exports = api;
  root.dnCart = api;
  if (typeof document !== 'undefined' && typeof fetch === 'function') api.boot();
})(typeof window !== 'undefined' ? window : globalThis, function (root) {
  'use strict';

  /* ---------- pure maths ---------- */
  function num(v) { var n = Number(v); return isFinite(n) ? n : 0; }
  function whole(v) { return Math.max(0, Math.floor(num(v))); }
  function isSystem(line) { return !!line && line.system === true; }
  /** The e-voucher: a Shopify gift card line. Not drinks (ADR-044). */
  function isGiftCard(line) { return !!line && line.gift_card === true; }
  /** Drinks: neither a system line nor an e-voucher. */
  function isDrinks(line) { return !!line && !isSystem(line) && !isGiftCard(line); }
  /** A line that owes deposit: a drink, bcrs true, a positive pack_qty and quantity. */
  function isBcrs(line) { return isDrinks(line) && line.bcrs === true && whole(line.pack_qty) > 0 && whole(line.quantity) > 0; }
  /** Deposit containers the basket owes: Σ(pack_qty × cartons) over BCRS lines. Integer, never negative. */
  function depositQty(lines) {
    var total = 0;
    (lines || []).forEach(function (l) { if (isBcrs(l)) total += whole(l.pack_qty) * whole(l.quantity); });
    return total;
  }
  /** Cartons in the basket: quantity over every drinks line (e-vouchers are not cartons). */
  function cartons(lines) {
    var total = 0;
    (lines || []).forEach(function (l) { if (isDrinks(l)) total += whole(l.quantity); });
    return total;
  }
  /** What the header count shows: every line the shopper chose, e-vouchers included, system lines not. */
  function items(lines) {
    var total = 0;
    (lines || []).forEach(function (l) { if (l && !isSystem(l)) total += whole(l.quantity); });
    return total;
  }
  /** E-vouchers in the basket, every denomination together. */
  function giftCardQty(lines) {
    var total = 0;
    (lines || []).forEach(function (l) { if (isGiftCard(l)) total += whole(l.quantity); });
    return total;
  }
  /** The online cap (GIFT_CARD_MAX_PER_ORDER in brain/packages/domain/src/gift-card.ts). */
  var GIFT_CARD_MAX = 10;
  function giftCardMax(config) { var m = whole(config && config.gift_card_max); return m > 0 ? m : GIFT_CARD_MAX; }
  /** The most a stepper may ask for on this line: 999 for a drink (99 until 5 Oct 2026, when a shopper wanted more of one water); for an e-voucher, what the cap leaves after the others. */
  function lineMax(lines, key, config) {
    var line = null;
    (lines || []).some(function (l) { if (l && l.key === key) { line = l; return true; } return false; });
    if (!isGiftCard(line)) return 999;
    return Math.max(0, giftCardMax(config) - (giftCardQty(lines) - whole(line.quantity)));
  }
  /** Vouchers and nothing to deliver: the basket needs no slot, no delivery and no fee. */
  function giftCardOnly(lines) {
    return giftCardQty(lines) > 0 && cartons(lines) === 0;
  }
  /** A line a partner packs and delivers (ADR-025): dn-cart-data.liquid reads the PTR- SKU prefix. */
  function isPartner(line) { return !!line && line.partner === true; }
  /** Cartons on OUR van: the header count less partner lines. What a slot is held for (orders.ts cartons). */
  function vanCartons(lines) {
    var total = 0;
    (lines || []).forEach(function (l) { if (isDrinks(l) && !isPartner(l)) total += whole(l.quantity); });
    return total;
  }
  /** The deposit line, matched by variant id when known, else the first system line that is not the fee. */
  function depositLine(lines, variantId) {
    var found = null;
    (lines || []).some(function (l) {
      if (!isSystem(l) || l.role === 'fee') return false;
      if (variantId && num(l.variant_id) !== num(variantId)) return false;
      found = l; return true;
    });
    return found;
  }
  /** The small-order service fee line (role "fee", or the variant id when known). */
  function feeLine(lines, variantId) {
    var found = null;
    (lines || []).some(function (l) {
      if (!isSystem(l)) return false;
      if (variantId && num(l.variant_id) === num(variantId)) { found = l; return true; }
      if (l.role === 'fee') { found = l; return true; }
      return false;
    });
    return found;
  }
  /** Drinks at carton (undiscounted) prices, system lines and e-vouchers excluded: the service-fee basis (fees.ts rule 2). */
  function drinksCartonCents(lines) {
    var total = 0;
    (lines || []).forEach(function (l) { if (isDrinks(l)) total += whole(l.price) * whole(l.quantity); });
    return total;
  }
  /** 1 while the drinks are under the line (and there are drinks), else 0. Mirrors serviceFeeQty in fees.ts. */
  function feeQty(lines, belowCents) {
    var d = drinksCartonCents(lines); var below = whole(belowCents);
    return below > 0 && d > 0 && d < below ? 1 : 0;
  }
  /** What one request must do so the fee line matches the basket (same shape as plan). */
  function feePlan(lines, feeVariantId, belowCents) {
    var want = feeQty(lines, belowCents);
    var line = feeLine(lines, feeVariantId);
    var have = line ? whole(line.quantity) : 0;
    if (want === have) return { op: 'none', quantity: want };
    if (!line) {
      if (!feeVariantId) return { op: 'none', quantity: want, reason: 'no-fee-variant' };
      return { op: 'add', quantity: want, variant_id: feeVariantId };
    }
    if (want === 0) return { op: 'remove', quantity: 0, key: line.key };
    return { op: 'change', quantity: want, key: line.key };
  }
  /** What one request must do so the deposit line matches the basket.
      {op:'none'|'add'|'change'|'remove', quantity, key?, variant_id?, reason?} */
  function plan(lines, depositVariantId) {
    var want = depositQty(lines);
    var line = depositLine(lines, depositVariantId);
    var have = line ? whole(line.quantity) : 0;
    if (want === have) return { op: 'none', quantity: want };
    if (!line) {
      if (!depositVariantId) return { op: 'none', quantity: want, reason: 'no-deposit-variant' };
      return { op: 'add', quantity: want, variant_id: depositVariantId };
    }
    if (want === 0) return { op: 'remove', quantity: 0, key: line.key };
    return { op: 'change', quantity: want, key: line.key };
  }

  /* ---------- DOM: data ---------- */
  function parseJson(el) { try { return el ? JSON.parse(el.textContent) : null; } catch (e) { return null; } }
  function readData(doc) {
    var lines = [];
    (doc || document).querySelectorAll('script[data-dn-cart-line]').forEach(function (s) { var l = parseJson(s); if (l) lines.push(l); });
    return { lines: lines, config: parseJson((doc || document).querySelector('script[data-dn-cart-config]')) || {} };
  }
  var state = { lines: [], config: {} };
  var routes = function () { return (root.routes || {}); };
  var DATA_SECTION = 'dn-cart-data';
  var BUBBLE_SECTION = 'cart-icon-bubble';
  function warn(e) { if (root.console) console.warn('dn-cart:', e && e.message ? e.message : e); }

  function render() {
    var n = items(state.lines);
    document.querySelectorAll('[data-dn-cart-count]').forEach(function (el) {
      var was = parseInt(el.textContent, 10) || 0;
      el.textContent = n > 0 ? String(n) : '';
      // The badge nods once when the count rises (dn.css .is-bump), never when it falls.
      if (n > was) { el.classList.remove('is-bump'); void el.offsetWidth; el.classList.add('is-bump'); }
    });
    // The hidden count text and the header link's name were printed at page load and never moved, so a
    // screen reader heard the old number after every add (Sprint 8 design pass).
    var said = n + (n === 1 ? ' item' : ' items');
    document.querySelectorAll('[data-dn-cart-count-sr]').forEach(function (el) { el.textContent = said; });
    var link = document.getElementById('cart-icon-bubble');
    if (link) link.setAttribute('aria-label', 'Cart, ' + said);
    // Belt and braces: the deposit line is never user-editable (Liquid renders no control).
    document.querySelectorAll('[data-dn-deposit-line] input, [data-dn-deposit-line] button, [data-dn-deposit-line] [data-dn-line-remove], [data-dn-deposit-line] [data-dn-stepper], [data-dn-fee-line] input, [data-dn-fee-line] button, [data-dn-fee-line] [data-dn-line-remove]').forEach(function (el) { el.remove(); });
    syncTiles();
    syncGiftCardForm();
    // A response that was already on its way carries the basket as it was a moment ago, and swapping it in
    // would walk a stepper backwards under the shopper's finger. Anything still asked for or in flight wins
    // on screen until the server confirms it (8 Sep 2026: "added but skipped back a number").
    [wanted, inFlight].forEach(function (pending) {
      Object.keys(pending).forEach(function (key) {
        var li = document.querySelector('[data-dn-line-key="' + key + '"]');
        var input = li && li.querySelector('[data-dn-line-qty]');
        if (input) input.value = whole(pending[key]);
      });
    });
    try { document.dispatchEvent(new CustomEvent('dn:cart', { detail: { lines: state.lines, config: state.config, cartons: cartons(state.lines), giftCards: giftCardQty(state.lines), giftCardOnly: giftCardOnly(state.lines), vanCartons: vanCartons(state.lines), deposit: depositQty(state.lines), fee: feeQty(state.lines, state.config.fee_below_cents) } })); } catch (e) {}
  }

  /** Sections this page renders from the cart (each carries data-dn-cart-section="<section id>"). */
  function pageSections() {
    var ids = [];
    document.querySelectorAll('[data-dn-cart-section]').forEach(function (el) { var id = el.getAttribute('data-dn-cart-section'); if (id && ids.indexOf(id) < 0) ids.push(id); });
    return ids;
  }
  function swapSections(sections) {
    if (!sections) return;
    Object.keys(sections).forEach(function (id) {
      if (id === DATA_SECTION || !sections[id]) return;
      var html = new DOMParser().parseFromString(sections[id], 'text/html');
      document.querySelectorAll('[data-dn-cart-section="' + id + '"]').forEach(function (target) {
        var sel = target.getAttribute('data-dn-cart-selector') || '.js-contents';
        var from = html.querySelector(sel), to = target.querySelector(sel) || target;
        if (from) to.innerHTML = from.innerHTML;
      });
    });
  }
  function absorb(sections) {
    if (sections && sections[DATA_SECTION]) state = readData(new DOMParser().parseFromString(sections[DATA_SECTION], 'text/html'));
  }
  function fetchData() {
    return fetch((routes().cart_url || '/cart') + '?section_id=' + DATA_SECTION, { cache: 'no-store', headers: { Accept: 'text/html' } })
      .then(function (r) { return r.text(); })
      .then(function (html) { return readData(new DOMParser().parseFromString(html, 'text/html')); });
  }
  function post(url, body, withSections) {
    if (withSections !== false) {
      body.sections = [DATA_SECTION].concat(pageSections()).join(',');
      body.sections_url = routes().cart_url || '/cart';
    }
    return fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) })
      /* j.errors is in the list because the AJAX endpoints answer a bad line reference with
         {"errors":"no valid id or line parameter"} and NO status field, so without it the throw
         said only "cart 400" and the real reason never reached the console. */
      .then(function (r) { return r.json().then(function (j) { if (!r.ok || j.status) throw new Error(j.description || j.message || j.errors || ('cart ' + r.status)); return j; }); });
  }
  function applyResponse(res) { absorb(res.sections); swapSections(res.sections); render(); }
  /* A write that changes nothing and comes back with the page's sections, so the screen can be put
     back in step with the server after a failed change. fetchData() is not enough on that path: it
     re-reads dn-cart-data only, which is the JSON the script thinks with, not the Liquid the shopper
     reads. That is exactly how the basket sat showing the previous quantity's money. */
  function resync() { return post(routes().cart_update_url || '/cart/update', {}).then(applyResponse); }
  /* Shopify's 1-based line number for a cart line, resolved against the CURRENT data at send time.
     snippets/dn-cart-data.liquid emits one script tag per `cart.items` in cart order, so the index
     into state.lines IS the line number.

     Why not keep addressing lines by item.key: SHOPIFY RE-KEYS A LINE WHENEVER ITS DISCOUNT
     ALLOCATION CHANGES, which the spend-ladder Function makes happen on nearly every press once a
     basket is discounted. Measured on the live cart, same variant, quantity the only thing moving:
       qty 4 -> key ...60b039fb  discount 0
       qty 5 -> key ...635db8f8  discount 293   <- re-keyed
       qty 6 -> key ...02cd5272  discount 351   <- re-keyed
     A press queued behind an in-flight press captured the key BEFORE that response landed, so it
     went out holding a key the cart no longer had, and got a 400.

     The key lookup wins when it still resolves. The variant fallback is what covers the re-key,
     since variant_id is stable across it. System lines are skipped in the fallback so a re-keyed
     drinks line can never be mistaken for the deposit or the fee. */
  function lineNumber(lines, key, variantId) {
    lines = lines || [];
    var i;
    for (i = 0; i < lines.length; i++) if (lines[i] && lines[i].key === key) return i + 1;
    if (variantId === null || variantId === undefined) return 0;
    for (i = 0; i < lines.length; i++) {
      var l = lines[i];
      if (l && !isSystem(l) && num(l.variant_id) === num(variantId)) return i + 1;
    }
    return 0;
  }

  /* ---------- one queue for every cart request ---------- */
  var queue = [], running = false;
  function enqueue(task) { queue.push(task); pump(); }
  function pump() {
    if (running) return;
    var t = queue.shift(); if (!t) return;
    running = true;
    Promise.resolve().then(t).catch(warn).then(function () { running = false; pump(); });
  }

  /* Deposit reconcile: plan from the current data, one request, then check again (the basket
     may have moved while the request was out). */
  var failures = 0;
  function apply(p) {
    var r = routes();
    if (p.op === 'add') return post(r.cart_add_url || '/cart/add', { items: [{ id: p.variant_id, quantity: p.quantity }] });
    return post(r.cart_change_url || '/cart/change', { id: p.key, quantity: p.quantity });
  }
  /** Deposit first, then the small-order fee: the next request the basket needs, or null. */
  function nextPlan(lines, config) {
    var p = plan(lines, config.deposit_variant_id);
    if (p.op !== 'none') return p;
    var f = feePlan(lines, config.fee_variant_id, config.fee_below_cents);
    return f.op !== 'none' ? f : null;
  }
  function reconcile() {
    if (failures >= 3) return null; // the store rejected a system line repeatedly; the Function is the backstop
    var p = nextPlan(state.lines, state.config);
    if (!p) return null;
    return apply(p).then(function (res) { failures = 0; applyResponse(res); return reconcile(); }, function (e) { failures += 1; throw e; });
  }
  var syncTimer = null, syncQueued = false;
  function syncTask() { syncQueued = false; return fetchData().then(function (d) { state = d; render(); return reconcile(); }); }
  function schedule() {
    if (syncTimer) clearTimeout(syncTimer);
    syncTimer = setTimeout(function () { syncTimer = null; if (!syncQueued) { syncQueued = true; enqueue(syncTask); } }, 120);
  }

  /* ---------- S3-06: line steppers, remove, delivery notes ---------- */
  var wanted = {};   // key → quantity asked for and not yet sent; one task per key in the queue
  var inFlight = {}; // key → quantity currently being sent, so a late response cannot undo it on screen
  function setLine(key, qty) {
    qty = whole(qty);
    var li = document.querySelector('[data-dn-line-key="' + key + '"]');
    if (li) { li.setAttribute('data-dn-busy', '1'); var input = li.querySelector('[data-dn-line-qty]'); if (input) input.value = qty; }
    /* The variant is read NOW, while the key is still known to be good, and carried into the task.
       It is what lets the line be found again if the in-flight response re-keys it first. */
    var variantId = null;
    (state.lines || []).some(function (l) { if (l && l.key === key) { variantId = l.variant_id; return true; } return false; });
    var queued = key in wanted;
    wanted[key] = qty;
    if (queued) return;
    enqueue(function () {
      var q = wanted[key]; delete wanted[key]; inFlight[key] = q;
      var done = function () { delete inFlight[key]; };
      /* Resolved HERE, not at press time: a task queued behind another goes out after that one's
         response has already landed and possibly re-keyed this line. */
      var n = lineNumber(state.lines, key, variantId);
      var body = n > 0 ? { line: n, quantity: q } : { id: key, quantity: q };
      return post(routes().cart_change_url || '/cart/change', body)
        .then(function (res) { done(); applyResponse(res); announce(q === 0 ? 'Removed from your basket' : 'Basket updated'); return reconcile(); })
        /* resync(), not fetchData(): a change that failed must never leave the shopper reading the
           previous quantity's money. And say so out loud - warn() is console-only, so a press that
           did nothing used to look like an unresponsive button. */
        .catch(function (e) {
          done(); warn(e);
          announce('Your basket did not change. Please try again.');
          return resync().catch(function (e2) { warn(e2); return fetchData().then(function (d) { state = d; render(); }); });
        });
    });
  }
  /* Add a variant through the same queue and the same section swap as a stepper press, then settle
     the deposit and fee (Sprint 5, 11 Sep 2026: the basket's gap-filler). Dawn's product-form path
     re-reads dn-cart-data only and swaps the page's sections just when a deposit or fee moves, so an
     add from inside the basket through it left the summary showing the old total. Resolves true when
     the add landed, false when it did not (the basket is re-synced either way). */
  function addItem(variantId, qty) {
    return new Promise(function (resolve) {
      enqueue(function () {
        return post(routes().cart_add_url || '/cart/add', { items: [{ id: num(variantId), quantity: whole(qty) || 1 }] })
          .then(function (res) {
            /* /cart/add answers with the added items plus the requested sections; the swap needs
               only the sections, which ride on the same response. */
            applyResponse(res); announce('Added to your basket'); resolve(true); return reconcile();
          })
          .catch(function (e) {
            warn(e); announce('That did not go in. Please try again.'); resolve(false);
            return resync().catch(warn);
          });
      });
    });
  }
  /* Tile steppers (FairPrice, 7 Sep 2026): each tile carries data-variant-id; the stepper shows with the line's quantity
     when the variant is in the basket, the Add form otherwise. Buttons change the line through setLine. */
  function lineForVariant(variantId) {
    var found = null;
    state.lines.some(function (l) { if (!isSystem(l) && num(l.variant_id) === num(variantId)) { found = l; return true; } return false; });
    return found;
  }
  function syncTiles() {
    document.querySelectorAll('[data-dn-tile-cart]').forEach(function (t) {
      var line = lineForVariant(t.getAttribute('data-variant-id'));
      // A press still travelling wins over the basket the last response described, so a tile never
      // counts backwards under the shopper's finger.
      var q = line ? lineQty(line.key, null) : 0;
      var form = t.querySelector('[data-dn-tile-form]'), st = t.querySelector('[data-dn-tile-stepper]'), out = t.querySelector('[data-dn-tile-qty]');
      if (out) out.textContent = String(q);
      // Keep keyboard focus when the control under it is swapped out: Add to cart becomes the stepper,
      // and the stepper going to 0 becomes Add to cart. Without this focus fell to <body>.
      var had = document.activeElement && t.contains(document.activeElement);
      var swap = form && st && form.hidden !== (q > 0);
      if (form) form.hidden = q > 0;
      if (st) st.hidden = q === 0;
      if (had && swap) {
        var next = q > 0 ? st.querySelector('[data-dn-tile-inc]') : form.querySelector('button[type=submit], button');
        if (next) next.focus();
      }
    });
  }
  function onTileClick(t) {
    var tile = t.closest('[data-dn-tile-cart]'); if (!tile) return;
    var line = lineForVariant(tile.getAttribute('data-variant-id')); if (!line) { syncTiles(); return; }
    // The same rule as the basket's stepper: press from what has been asked for, not from the last
    // answer the server gave, or two quick presses both ask for the same number and one is lost.
    var q = lineQty(line.key, null);
    var next = t.hasAttribute('data-dn-tile-inc') ? Math.min(lineMax(state.lines, line.key, state.config), q + 1) : q - 1;
    // Show it at once. setLine writes the basket's own input straight away; a tile's number only moved
    // when the server answered, which is the lag the owner saw on the product page (8 Sep 2026).
    var out = tile.querySelector('[data-dn-tile-qty]');
    if (out) out.textContent = String(Math.max(0, next));
    setLine(line.key, next);
  }
  /**
   * What this line is at, for a stepper about to add or take one away. In order: the quantity already
   * asked for and not yet sent, then the basket as the last response left it, then the input on screen.
   * Reading the input first was the bug (8 Sep 2026: "number + - seem glitchy, added but skipped
   * back a number stucked"): a press that lands while the section is being swapped reads the value the
   * server has just superseded, asks for a number the basket already holds, and the press does nothing.
   */
  function lineQty(key, li) {
    if (key in wanted) return whole(wanted[key]);
    if (key in inFlight) return whole(inFlight[key]);
    var known = null;
    state.lines.some(function (l) { if (l && l.key === key) { known = l; return true; } return false; });
    if (known) return whole(known.quantity);
    var input = li && li.querySelector('[data-dn-line-qty]');
    return whole(input ? input.value : 1);
  }
  /* The e-voucher page's quantity follows the basket: after an add, what is left of the cap is the most the
     stepper offers, and at the cap the button says why it is off (snippets/dn-gift-card.liquid). */
  function syncGiftCardForm() {
    var left = Math.max(0, giftCardMax(state.config) - giftCardQty(state.lines));
    document.querySelectorAll('[data-dn-gc-qty]').forEach(function (i) {
      i.setAttribute('max', String(Math.max(1, left)));
      if (whole(i.value) > Math.max(1, left)) i.value = String(Math.max(1, left));
    });
    document.querySelectorAll('[data-dn-gc-add]').forEach(function (b) {
      if (b.hasAttribute('data-dn-gc-unavailable')) return;
      b.disabled = left === 0;
      var label = b.querySelector('[data-dn-gc-add-label]');
      if (label) label.textContent = left === 0 ? giftCardMax(state.config) + ' e-vouchers in your basket, the most per order' : 'Add to basket';
    });
    document.querySelectorAll('[data-dn-gc-basket]').forEach(function (a) { a.hidden = giftCardQty(state.lines) === 0; });
  }
  function announce(text) { var el = document.querySelector('[data-dn-cart-status]'); if (el) el.textContent = text; }
  var attrTimer = null;
  function saveAttributes() {
    if (attrTimer) clearTimeout(attrTimer);
    attrTimer = setTimeout(function () {
      var attrs = {};
      document.querySelectorAll('[data-dn-attr]').forEach(function (i) {
        var m = /^attributes\[(.+)\]$/.exec(i.name); if (!m) return;
        attrs[m[1]] = i.type === 'checkbox' ? (i.checked ? i.value : '') : i.value;
      });
      enqueue(function () { return post(routes().cart_update_url || '/cart/update', { attributes: attrs }, false); });
    }, 400);
  }
  /* PDP with several variants: the buy box and the pinned bar follow the chosen variant's line. */
  function onVariantChange(e) {
    var sel = e.target; if (!sel || !sel.matches || !sel.matches('[data-dn-tile-cart] select[name="id"]')) return;
    document.querySelectorAll('[data-dn-atc-main][data-dn-tile-cart], [data-dn-atc-sticky][data-dn-tile-cart]').forEach(function (t) { t.setAttribute('data-variant-id', sel.value); });
    syncTiles();
  }
  function onClick(e) {
    var t = e.target.closest('[data-dn-line-dec], [data-dn-line-inc], [data-dn-line-remove], [data-dn-open-cart], [data-dn-drawer-close], [data-dn-tile-dec], [data-dn-tile-inc]');
    if (!t) return;
    if (t.hasAttribute('data-dn-tile-dec') || t.hasAttribute('data-dn-tile-inc')) { onTileClick(t); return; }
    var li = t.closest('[data-dn-line]');
    if (t.hasAttribute('data-dn-line-remove') && li) { e.preventDefault(); setLine(li.getAttribute('data-dn-line-key'), 0); return; }
    if ((t.hasAttribute('data-dn-line-dec') || t.hasAttribute('data-dn-line-inc')) && li) {
      var key = li.getAttribute('data-dn-line-key');
      var cap = lineMax(state.lines, key, state.config);
      if (t.hasAttribute('data-dn-line-inc') && lineQty(key, li) >= cap) { announce('Up to ' + giftCardMax(state.config) + ' e-vouchers per order'); return; }
      setLine(key, t.hasAttribute('data-dn-line-inc') ? Math.min(cap, lineQty(key, li) + 1) : lineQty(key, li) - 1); return;
    }
    if (t.hasAttribute('data-dn-open-cart')) { var d = drawer(); if (d && !isCartPage()) { e.preventDefault(); d.open(t); } return; }
    if (t.hasAttribute('data-dn-drawer-close')) { var dd = drawer(); if (dd) dd.close(); }
  }
  function onChange(e) {
    var t = e.target;
    if (t.hasAttribute('data-dn-line-qty')) { var li = t.closest('[data-dn-line]'); if (li) { var k = li.getAttribute('data-dn-line-key'); setLine(k, Math.min(lineMax(state.lines, k, state.config), whole(t.value))); } return; }
    if (t.hasAttribute('data-dn-attr')) saveAttributes();
  }
  function isCartPage() { var m = document.querySelector('main[data-template]'); return !!m && m.getAttribute('data-template') === 'cart'; }
  function drawer() { return document.querySelector('cart-drawer'); }

  /* ---------- S3-06: <cart-drawer> (what product-form.js expects after an add) ---------- */
  function defineDrawer() {
    if (typeof customElements === 'undefined' || customElements.get('cart-drawer')) return;
    var C = function () { return Reflect.construct(HTMLElement, [], C); };
    C.prototype = Object.create(HTMLElement.prototype);
    C.prototype.constructor = C;
    C.prototype.connectedCallback = function () {
      var self = this;
      this.addEventListener('keydown', function (e) { if (e.key === 'Escape') self.close(); });
    };
    C.prototype.getSectionsToRender = function () {
      return [
        { id: 'dn-cart-drawer', section: 'dn-cart-drawer', selector: '.js-contents' },
        { id: BUBBLE_SECTION, section: BUBBLE_SECTION, selector: '.shopify-section' }
      ];
    };
    C.prototype.renderContents = function (parsedState) {
      if (parsedState && parsedState.sections) swapSections(parsedState.sections);
      // A tile add, and since 7 Sep the PDP add too, stays on the page (FairPrice): the form becomes a stepper bound to the
      // line on the next sync (the PDP buy box and its pinned bar are [data-dn-tile-cart] as well), the drawer stays shut.
      // Not keyed on the focused element: a programmatic submit (specs, or a keyboard add from a select) has none,
      // and every add form on the store is a tile or the PDP, so the drawer never opens on an add.
      announce('Added to your basket');
    };
    C.prototype.setActiveElement = function (el) { this.activeElement = el; };
    C.prototype.open = function (opener) {
      if (opener) this.activeElement = opener;
      this.classList.add('is-open');
      document.body.classList.add('dn-drawer-open');
      this.setAttribute('aria-hidden', 'false');
      var panel = this.querySelector('.dn-drawer__panel'); var close = this.querySelector('.dn-drawer__close');
      if (close) close.focus(); else if (panel) panel.focus();
    };
    C.prototype.close = function () {
      this.classList.remove('is-open');
      document.body.classList.remove('dn-drawer-open');
      this.setAttribute('aria-hidden', 'true');
      if (this.activeElement && this.activeElement.focus) this.activeElement.focus();
    };
    customElements.define('cart-drawer', C);
  }

  function boot() {
    function init() {
      defineDrawer();
      var d = drawer(); if (d) d.setAttribute('aria-hidden', 'true');
      state = readData(document); render();
      // Dawn declares these with const/function at script top level, so they are lexical globals,
      // not window properties: read them by name.
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        subscribe(PUB_SUB_EVENTS.cartUpdate, function (ev) { if (!ev || ev.source !== 'dn-cart') schedule(); });
      }
      document.addEventListener('dn:cart:refresh', schedule);
      document.addEventListener('click', onClick);
      document.addEventListener('change', onVariantChange);
      document.addEventListener('change', onChange);
      // Repair a stale basket on load without a request when nothing is owed.
      if (nextPlan(state.lines, state.config)) schedule();
    }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  }

  return { depositQty: depositQty, cartons: cartons, items: items, giftCardQty: giftCardQty, giftCardMax: giftCardMax, lineMax: lineMax, giftCardOnly: giftCardOnly, isGiftCard: isGiftCard, vanCartons: vanCartons, isPartner: isPartner, depositLine: depositLine, plan: plan, isBcrs: isBcrs, feeLine: feeLine, feeQty: feeQty, feePlan: feePlan, drinksCartonCents: drinksCartonCents, nextPlan: nextPlan, readData: readData, lineNumber: lineNumber, sync: schedule, setLine: setLine, addItem: addItem, boot: boot };
});
